import { openPopup } from "@/helper/DxHelper";
import { CalculateGrid } from "@/redux/uq/uq-actions";
import { uqActions } from "@/redux/uq/uq-slice";
import { Tooltip, ContextMenu } from "devextreme-react";
import { Position } from "devextreme-react/cjs/autocomplete";
import { formatNumber } from "devextreme/localization";
import { Item } from "devextreme/ui/accordion";
import { useAppDispatch } from "@/hooks/useAppDispatch";
import { useAppSelector } from "@/hooks/useAppSelector";
import { PopupRef } from "devextreme-react/cjs/popup";
import { ColumnCellTemplateData } from "devextreme/ui/data_grid";
import { NPVRow, UPRow } from "@/types/types";
import { npvActions, NPVHeader } from "@/redux/npv/npv-slice";
import { CalcGrid } from "@/redux/npv/npv-actions";
import { cn } from "@/helper/helper";

type Props = {
  data: ColumnCellTemplateData<UPRow | NPVRow>;
  updateRef?: React.RefObject<PopupRef>;
  prepopulatePopupRef?: React.RefObject<PopupRef>;
  isNpv?: boolean;
  hasContextMenu?: boolean;
};

export default function GridCell({
  data,
  prepopulatePopupRef,
  updateRef,
  hasContextMenu,
  isNpv,
}: Props) {
  const dispatch = useAppDispatch();
  const {
    gridSnapshotAges: { age1 },
    gridSet,
    indexShift,
  } = useAppSelector((state) => state.uq);
  const { settings: npvSettings } = useAppSelector((state) => state.npv);
  const { age1: age1Now } = useAppSelector((state) => state.household);
  const column = data.column?.dataField as NPVHeader;

  const excludedValueCell = (data.row.data as NPVRow)[column]?.isExcluded;
  const isCurrAgeRowLower = data.row.data.Age.value && age1 >= data.row.data.Age.value;

  const cellExcludedByYearsToRetire = data.row.data.Age.value
    ? age1 + npvSettings?.yearsToRetire > data.row.data.Age.value
    : null;

  const value = data.value;
  const isFirstRowRetirementAssets =
    data.row.data.Age.value === age1Now && column?.toLocaleLowerCase().includes("retirement");

  const isAnySocialSocurity = column.toLocaleLowerCase().includes("socialsecurity");

  const ctxMenuArr: Item[] = [
    {
      text: "Reset",
      icon: "revert",
      visible: !isAnySocialSocurity && !isFirstRowRetirementAssets && value.manualChange,
    },
    { text: "Note", icon: "comment" },
    {
      text: "Prepopulate cells",
      icon: "cellproperties",
      visible: !isFirstRowRetirementAssets && !isAnySocialSocurity,
    },
  ];

  return (
    <>
      <div
        id={`${column}-${data.rowIndex}`}
        className={cn("relative px-2 py-[14px] text-sm print:px-1 print:py-1 print:!text-[10px]", {
          "text-danger": value.value < 0,
          italic: data.row.data.Age.value && age1Now > data.row.data.Age.value,
          "cursor-pointer":
            !isFirstRowRetirementAssets || (hasContextMenu && !cellExcludedByYearsToRetire),
          "bg-neutral-400": isFirstRowRetirementAssets,
          "!bg-accent-700": excludedValueCell && isNpv,
          "bg-secondary": isNpv && !excludedValueCell,
        })}
      >
        {formatNumber(value.value, "currency").replaceAll("(", "").replaceAll(")", "")}
        {value?.note?.length ? (
          <img className="absolute right-0 top-0 print:h-2 print:w-2" src="/icons/note-cell.svg" />
        ) : null}

        {isFirstRowRetirementAssets && (
          <Tooltip
            target={`#${column}-${data.rowIndex}`}
            showEvent="mouseenter"
            hideEvent="mouseleave"
          >
            The first row of "Retirement Assets" column can only be changed via Salesforce's "Last
            Retirement Assets" field.
          </Tooltip>
        )}
      </div>

      {!isNpv ? (
        <ContextMenu
          items={ctxMenuArr}
          width={200}
          target={`#${column}-${data.rowIndex}`}
          onItemClick={(e) => {
            const newGrid = structuredClone(gridSet);
            const currentIndex = data.rowIndex + indexShift;
            switch (e.itemData?.text?.toLowerCase()) {
              case "reset": {
                const lastIndex = newGrid.length - 1;

                if (newGrid[currentIndex][column].manualChange) {
                  for (let i = currentIndex; i <= lastIndex; i++) {
                    if (newGrid[i][column].manualChange) {
                      newGrid[i][column] = {
                        value: value.defaultValue,
                        defaultValue: value.defaultValue,
                        note: value.note,
                        manualChange: false,
                        isExcluded: newGrid[i][column].isExcluded,
                      };
                    }
                    if (i !== lastIndex) if (newGrid[i + 1][column]?.manualChange) break;
                  }
                }

                newGrid[currentIndex][column] = {
                  value: value.defaultValue,
                  defaultValue: value.defaultValue,
                  note: value.note,
                  manualChange: false,
                  isExcluded: newGrid[currentIndex][column].isExcluded,
                };

                dispatch(
                  CalculateGrid(currentIndex > 1 ? currentIndex - 1 : currentIndex, newGrid)
                );
                break;
              }
              case "note":
                dispatch(
                  uqActions.setUpdateData({
                    value: value.value,
                    defaultValue: value.defaultValue,
                    manualChange: value.manualChange,
                    note: value.note ?? "",
                    id: `${column}-${data.rowIndex}`,
                    col: column,
                    rowIndex: data.rowIndex,
                    row: indexShift + data.rowIndex,
                  })
                );
                if (updateRef) openPopup(updateRef);
                break;

              case "prepopulate cells":
                dispatch(
                  uqActions.setUpdateData({
                    value: value.value,
                    defaultValue: value.defaultValue,
                    manualChange: value.manualChange,
                    note: value.note ?? "",
                    id: `${column}-${data.rowIndex}`,
                    col: column,
                    rowIndex: data.rowIndex,
                    row: indexShift + data.rowIndex,
                  })
                );
                setTimeout(() => {
                  if (prepopulatePopupRef) openPopup(prepopulatePopupRef);
                }, 300);
                break;
            }
          }}
        />
      ) : null}

      {value.note ? (
        <Tooltip
          target={`#${column}-${data.rowIndex}`}
          showEvent="mouseenter"
          hideEvent="mouseleave"
          hideOnOutsideClick={false}
        >
          <Position at={"right top"} my={"bottom"} offset={{ x: 70, y: 30 }} />
          <div className="h-fit max-w-[100px] whitespace-pre-line break-words">{value.note}</div>
        </Tooltip>
      ) : null}

      {hasContextMenu && !cellExcludedByYearsToRetire && isNpv ? (
        <ContextMenu
          dataSource={[
            gridSet[(data?.row?.rowIndex ?? 0) + (indexShift ?? 0)][column]?.isExcluded
              ? { text: "Undo", icon: "undo" }
              : { text: "Exclude value", icon: "remove" },
          ]}
          width={200}
          target={`#${column}-${data.row.rowIndex}`}
          onItemClick={async (e) => {
            const newGrid = structuredClone(gridSet);

            const currentRow = newGrid[data.row.rowIndex + indexShift];
            const currentCell = currentRow[column];

            switch (e.itemData?.text?.toLowerCase()) {
              case "exclude value":
                currentCell.isExcluded = true;
                dispatch(npvActions.setGrid(newGrid));
                dispatch(uqActions.setGrid(newGrid));
                dispatch(CalcGrid(gridSet));
                dispatch(npvActions.setIsModified(true));
                break;
              case "undo":
                currentCell.isExcluded = false;
                dispatch(npvActions.setGrid(newGrid));
                dispatch(uqActions.setGrid(newGrid));
                dispatch(CalcGrid(gridSet));
                dispatch(npvActions.setIsModified(true));
                break;
            }
          }}
        />
      ) : null}
    </>
  );
}
